// /admin/v1/workflows — workflow_templates registry.

import { get, list, patch, post } from './client';
import type { ListResult } from './client';

export type WorkflowKind =
  | 'character_gen'
  | 'age_progression'
  | 'past_life'
  | 'face_swap'
  | 'lora_train'
  | string;

export type WorkflowStatus = 'draft' | 'active' | 'archived';

export interface WorkflowTemplate {
  code: string;
  name: string;
  kind: WorkflowKind;
  version: number;
  status: WorkflowStatus;
  cost_diamonds?: number;
  provider_chain?: string[];
  final_face_swap?: boolean;
  definition?: Record<string, unknown>;
  input_schema?: Record<string, unknown>;
  output_schema?: Record<string, unknown>;
  /** Per-step LLM system prompts. shape: { system, story, ... }. P0.1 lego-ization. */
  prompts?: Record<string, string>;
  created_at?: string;
  updated_at?: string;
}

export interface ListWorkflowsQuery {
  q?: string;
  status?: WorkflowStatus;
  kind?: WorkflowKind;
  page?: number;
  page_size?: number;
}

export function listWorkflows(
  q: ListWorkflowsQuery = {},
): Promise<ListResult<WorkflowTemplate>> {
  return list<WorkflowTemplate>('/admin/v1/workflows', q);
}

export function getWorkflow(code: string): Promise<WorkflowTemplate> {
  return get<WorkflowTemplate>(`/admin/v1/workflows/${code}`);
}

export interface CreateWorkflowInput {
  code: string;
  name: string;
  kind: WorkflowKind;
  definition?: Record<string, unknown>;
  input_schema?: Record<string, unknown>;
  output_schema?: Record<string, unknown>;
  provider_chain?: string[];
  cost_diamonds?: number;
  final_face_swap?: boolean;
  prompts?: Record<string, string>;
}

/** Definition patch helper — 改定价 (cost_diamonds 生成消耗 / unlock_diamonds 解锁价,
 * 存于 definition JSONB)。传入合并好的完整 definition 整体 PATCH。 */
export function updateWorkflowDefinition(
  code: string,
  definition: Record<string, unknown>,
): Promise<WorkflowTemplate> {
  return patch<WorkflowTemplate>(`/admin/v1/workflows/${code}`, { definition });
}

/** Prompts-only patch helper (most common admin edit — content team owns LLM prompts). */
export function updateWorkflowPrompts(
  code: string,
  prompts: Record<string, string>,
): Promise<WorkflowTemplate> {
  return patch<WorkflowTemplate>(`/admin/v1/workflows/${code}`, { prompts });
}

export function createWorkflow(input: CreateWorkflowInput): Promise<WorkflowTemplate> {
  return post<WorkflowTemplate>('/admin/v1/workflows', input);
}

export function updateWorkflow(
  code: string,
  input: Partial<CreateWorkflowInput>,
): Promise<WorkflowTemplate> {
  return patch<WorkflowTemplate>(`/admin/v1/workflows/${code}`, input);
}
